'use client';

import { Check } from '@phosphor-icons/react/ssr';
import {
  Checkbox as CheckboxPrimitive,
  RadioGroup as RadioGroupPrimitive,
} from 'radix-ui';
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useId,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { radioCircle, selectionMarkPop } from '../ui/radio-group';
import { Badge } from './badge';
import { checkboxBox } from './checkbox-styles';
import { useFieldControl, useFieldLabelId } from './field';

type GroupContextValue =
  | { type: 'radio'; disabled: boolean }
  | {
      type: 'checkbox';
      disabled: boolean;
      values: string[];
      toggle: (value: string, checked: boolean) => void;
    };

const GroupContext = createContext<GroupContextValue | null>(null);

type CommonGroupProps = {
  /** Stack the cards (default) or lay them out in a wrapping row. */
  orientation?: 'vertical' | 'horizontal';
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

export type ChoiceCardGroupProps =
  | (CommonGroupProps & {
      /** Figma Type=Radio (default): one choice. */
      type?: 'radio';
      value?: string;
      defaultValue?: string;
      onValueChange?: (value: string) => void;
      name?: string;
      required?: boolean;
    })
  | (CommonGroupProps & {
      /** Figma Type=Checkbox: any number of choices. */
      type: 'checkbox';
      value?: string[];
      defaultValue?: string[];
      onValueChange?: (value: string[]) => void;
    });

const group = tv({
  base: 'grid gap-3',
  variants: {
    orientation: {
      vertical: 'grid-cols-1',
      // Equal-width cards in a row, wrapping below 240px each.
      horizontal: 'grid-cols-[repeat(auto-fit,minmax(240px,1fr))]',
    },
  },
});

/**
 * A set of Choice Cards. `type="radio"` (default) is a Radix RadioGroup: Tab
 * enters on the selected card, arrow keys move and select. `type="checkbox"`
 * is a group of Radix Checkboxes (`value` is an array): Tab visits each card,
 * Space toggles. Inside a Field, the Field label names the group.
 */
export function ChoiceCardGroup(props: ChoiceCardGroupProps) {
  const fieldLabelId = useFieldLabelId();
  const { orientation = 'vertical', className, children } = props;
  const {
    id,
    disabled = false,
    'aria-describedby': describedBy,
  } = useFieldControl({ id: props.id, disabled: props.disabled }) as {
    id?: string;
    disabled?: boolean;
    'aria-describedby'?: string;
  };
  const labelledBy =
    props['aria-labelledby'] ?? (props['aria-label'] ? undefined : fieldLabelId);
  const [checkedValues, setCheckedValues] = useState<string[]>(
    props.type === 'checkbox' ? (props.defaultValue ?? []) : [],
  );

  const shared = {
    id,
    'aria-label': props['aria-label'],
    'aria-labelledby': labelledBy,
    'aria-describedby': props['aria-describedby'] ?? describedBy,
    'data-slot': 'choice-card-group',
    className: group({ orientation, className }),
  };

  if (props.type === 'checkbox') {
    const values = props.value ?? checkedValues;
    const toggle = (value: string, checked: boolean) => {
      const next = checked ? [...values, value] : values.filter((v) => v !== value);
      setCheckedValues(next);
      props.onValueChange?.(next);
    };
    return (
      <GroupContext value={{ type: 'checkbox', disabled, values, toggle }}>
        <div role="group" aria-disabled={disabled || undefined} {...shared}>
          {children}
        </div>
      </GroupContext>
    );
  }

  return (
    <GroupContext value={{ type: 'radio', disabled }}>
      <RadioGroupPrimitive.Root
        value={props.value}
        defaultValue={props.defaultValue}
        onValueChange={props.onValueChange}
        name={props.name}
        required={props.required}
        disabled={disabled}
        orientation={orientation}
        {...shared}
      >
        {children}
      </RadioGroupPrimitive.Root>
    </GroupContext>
  );
}

export type ChoiceCardProps = Omit<
  ComponentProps<'button'>,
  'title' | 'value' | 'children' | 'type'
> & {
  /** The option's value in its group. */
  value: string;
  /** Figma "Title" (Label/Medium) - names the option. */
  title: ReactNode;
  /** Figma "Description" (Body/Small) - one line on what it's for. */
  description?: ReactNode;
  /** Figma "Visual": an Aspect Glyph, icon tile or thumbnail, 40×40. */
  visual?: ReactNode;
  /** Figma "Badge": a Brand Solid Small Badge under the text, e.g. "Recommended". */
  badge?: ReactNode;
  disabled?: boolean;
};

/**
 * The Figma Choice Card (94:136), keyed on the attributes Radix sets on the
 * card (`data-state`, `data-disabled`).
 *
 * 16px padding, gap 12, radius/lg, 1px border/default on bg/surface. Hover:
 * border/strong-hover. Selected: brand/subtle fill + 2px brand/strong stroke
 * (1px border + 1px inset ring, as Figma's stroke + inner shadow; the card
 * must not grow when picked). Focus: Focus/Ring. Disabled:
 * bg/disabled, border/subtle, text/disabled and the visual at 50%.
 * Motion: a 0.99 press, and the radio dot / checkbox tick pops in
 * (`selectionMarkPop`); reduced motion keeps only the fades.
 */
const card = tv({
  slots: {
    root: [
      'group/choice bg-bg-surface border-border-default relative flex w-full items-start gap-3 rounded-lg border p-4 text-left outline-none',
      'transition-[background-color,border-color,box-shadow,scale] duration-[120ms] ease-out',
      'enabled:hover:border-border-strong-hover',
      // Press: felt, not seen - a large surface gets less than a Button's 0.97.
      'enabled:active:scale-[0.99] motion-reduce:enabled:active:scale-100',
      'data-[state=checked]:bg-brand-subtle data-[state=checked]:border-brand-strong data-[state=checked]:inset-ring-brand-strong data-[state=checked]:inset-ring-1',
      'data-[state=checked]:enabled:hover:border-brand-strong',
      'focus-visible:shadow-focus-ring',
      'disabled:bg-bg-disabled disabled:border-border-subtle disabled:cursor-not-allowed disabled:inset-ring-0',
    ],
    visual: [
      'flex size-10 shrink-0 items-center justify-center group-disabled/choice:opacity-50',
      'group-data-[state=checked]/choice:[--aspect-glyph-fill:var(--color-bg-surface)]',
    ],
    text: 'flex min-w-0 flex-1 flex-col items-start gap-0.5',
    title: 'text-label-md text-text-primary group-disabled/choice:text-text-disabled',
    description:
      'text-body-sm text-text-secondary group-disabled/choice:text-text-disabled',
    control: 'pointer-events-none',
  },
});

/**
 * Large, scannable option with a visual, title and description - plans,
 * export formats, templates (Figma: Components ↳ Choice Card ↳ Choice Card).
 * Use inside a ChoiceCardGroup: radio for one choice, checkbox for many. The
 * whole card is the hit target; Selected = 2px Deep Olive border, Pale Sage
 * fill and the checked control (never colour alone). The title names the
 * card, the description describes it.
 */
export function ChoiceCard({
  value,
  title,
  description,
  visual,
  badge,
  disabled: ownDisabled,
  className,
  ...props
}: ChoiceCardProps) {
  const context = useContext(GroupContext);
  if (!context) throw new Error('ChoiceCard must be used inside a ChoiceCardGroup');
  const id = useId();
  const disabled = ownDisabled || context.disabled;
  const styles = card();
  const radio = context.type === 'radio';
  const checked = radio ? undefined : context.values.includes(value);

  const content = (
    <>
      {visual && (
        <span data-slot="choice-card-visual" className={styles.visual()}>
          {visual}
        </span>
      )}
      <span className={styles.text()}>
        <span
          id={`${id}-title`}
          data-slot="choice-card-title"
          data-disabled={disabled || undefined}
          className={styles.title()}
        >
          {title}
        </span>
        {description && (
          <span
            id={`${id}-description`}
            data-slot="choice-card-description"
            data-disabled={disabled || undefined}
            className={styles.description()}
          >
            {description}
          </span>
        )}
        {badge && (
          <Badge tone="brand" variant="solid" size="sm" data-slot="choice-card-badge">
            {badge}
          </Badge>
        )}
      </span>
    </>
  );
  const naming = {
    'aria-labelledby': `${id}-title`,
    'aria-describedby': description ? `${id}-description` : undefined,
  };

  if (radio)
    return (
      <RadioGroupPrimitive.Item
        data-slot="choice-card"
        value={value}
        disabled={disabled}
        className={styles.root({ className })}
        {...naming}
        {...props}
      >
        {content}
        {/* The Figma Radio circle, drawn from the card's state. */}
        <RadioGroupPrimitive.Indicator forceMount asChild>
          <span
            data-slot="choice-card-control"
            className={radioCircle({ className: styles.control() })}
          >
            <span
              className={`size-[7px] rounded-full bg-current group-data-[state=unchecked]/choice:scale-40 group-data-[state=unchecked]/choice:opacity-0 ${selectionMarkPop}`}
            />
          </span>
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
    );

  return (
    <CheckboxPrimitive.Root
      data-slot="choice-card"
      value={value}
      checked={checked}
      onCheckedChange={(next) => context.toggle(value, next === true)}
      disabled={disabled}
      className={styles.root({ className })}
      {...naming}
      {...props}
    >
      {content}
      {/* The Figma Checkbox box, drawn from the card's state. */}
      <CheckboxPrimitive.Indicator forceMount asChild>
        <span
          data-slot="choice-card-control"
          className={checkboxBox({ className: styles.control() })}
        >
          <Check
            aria-hidden
            className={`group-data-[state=unchecked]/choice:scale-60 group-data-[state=unchecked]/choice:opacity-0 ${selectionMarkPop}`}
          />
        </span>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}
